import { Children, isValidElement, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { Button, Collapsible, CollapsibleRoot, CollapsibleTrigger, Icon } from '@sunwebgroup/ui-components';
import { splitAt } from 'ramda';
import { styled } from 'styled-components';
import { createPDPClickEvent, type PDPClickEventProps } from '@/scopes/products/infrastructure/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

const CollapsibleContent = styled(Collapsible)`
  // margin fix due to the overflow:hidden on the parent hidding the shadow of the children
  margin-left: -3px;
  margin-right: -3px;
`;

const CollapsibleContentWrapper = styled.div`
  // padding fix to negate the margin fix on the CollapsibleContent
  padding: 0 3px;
`;

const CollapseButton = styled(Button)`
  padding: unset;
`;

type ShowMoreProps = PropsWithChildren<{
  className?: string;
  closeLabel: string;
  minimumHiddenItems?: number;
  openLabel: string;
  visibleItems?: number;
  showMoreEvent?: PDPClickEventProps;
  showLessEvent?: PDPClickEventProps;
}>;

export function ShowMore({
  children,
  className,
  closeLabel,
  minimumHiddenItems = 2,
  openLabel,
  visibleItems = 3,
  showMoreEvent,
  showLessEvent,
}: ShowMoreProps) {
  const [open, setOpen] = useState(false);
  const { sendEvent } = useAnalytics();

  const lastCheckedIndex = Children.toArray(children)
    .map((child) => isValidElement(child) && (child.props.checked as boolean))
    .lastIndexOf(true);
  const isHiddenChildChecked = lastCheckedIndex >= visibleItems;

  const [shownChildren, hiddenChildren] = useMemo(
    () => splitAt(visibleItems, Children.toArray(children)),
    [children, visibleItems]
  );

  const hiddenChildrenCount = Children.count(hiddenChildren);
  const shouldHideChildren = hiddenChildrenCount >= minimumHiddenItems;

  useEffect(() => {
    if (!shouldHideChildren) {
      setOpen(false);
    }
  }, [shouldHideChildren]);

  useEffect(() => {
    if (isHiddenChildChecked && !open) {
      setOpen(true);
      if (showMoreEvent) {
        sendEvent(createPDPClickEvent(showMoreEvent));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled because we only want to listen to changes in 'isHiddenChildChecked' and not 'open'
  }, [isHiddenChildChecked]);

  const handleChange = () => {
    setOpen(!open);
    if (open && showLessEvent) {
      sendEvent(createPDPClickEvent(showLessEvent));
    }
    if (!open && showMoreEvent) {
      sendEvent(createPDPClickEvent(showMoreEvent));
    }
  };

  return (
    <div className={className}>
      {shownChildren}
      {!shouldHideChildren && hiddenChildren}
      {shouldHideChildren && hiddenChildren && (
        <CollapsibleRoot open={open} onOpenChange={handleChange} defaultOpen={isHiddenChildChecked}>
          <CollapsibleContent>
            <CollapsibleContentWrapper>{hiddenChildren}</CollapsibleContentWrapper>
          </CollapsibleContent>
          <CollapsibleTrigger asChild>
            <CollapseButton $hierarchy="tertiary" $fullSize>
              {open ? closeLabel : openLabel}
              {open ? <Icon glyph="IconChevronUpSmall" /> : <Icon glyph="IconChevronDownSmall" />}
            </CollapseButton>
          </CollapsibleTrigger>
        </CollapsibleRoot>
      )}
    </div>
  );
}
